import * as React from "react";
import { Text, Button } from "@uikit";
import { CourseReview, pluralize } from "@kalabo/golfsavers-common";
import ReviewItem from "./reviewItem";
import ReviewAvgItem from "./reviewAvgItem";
import StarRating from "../../starRating";
import Shield_Icon from "@images/icons/review_shield.svg";

interface IProps {
  reviews: CourseReview[];
  onShowModal?: () => void;
}

const CourseReviews = ({ reviews, onShowModal }: IProps) => {
  const [visible, setVisible] = React.useState(3);
  const [modalVisible, setModalVisible] = React.useState(false);
  if (reviews.length === 0) return null;

  const getAverageRating = (propName: any) => {
    const ratings = reviews.map(
      (review) => review[propName as keyof typeof CourseReviews]
    );
    const sum = ratings.reduce((a, b) => a + b, 0);
    return Number((sum / ratings.length).toFixed(1));
  };

  return (
    <>
      <div className="mb-10 border-t border-grey3 pt-10">
        <div className="flex justify-between items-center mb-[30px]">
          <Text element="h2" size={21} weight="semibold" color="black">
            Reviews
          </Text>
        </div>
        <div className="mb-10">
          <div className="flex gap-2 mb-4 justify-between items-center">
            <div className="flex gap-2 items-center">
              <div className="flex lg:mt-0">
                <StarRating
                  mode="small"
                  hideCount
                  rating={getAverageRating("overall")}
                  size="xlarge"
                />
              </div>
              <Text
                size={19}
                weight="semibold"
                color="black80"
                className="mr-3"
              >
                {getAverageRating("overall")} out of 5 ·{" "}
                {pluralize(reviews?.length, "review", "reviews")}
              </Text>
            </div>
          </div>
          <div className="sm:grid sm:grid-cols-2 gap-x-10">
            <ReviewAvgItem
              rating={getAverageRating("condition")}
              text="Course Condition"
            />
            <ReviewAvgItem
              rating={getAverageRating("facilities")}
              text="Facilities"
            />
            <ReviewAvgItem
              rating={getAverageRating("pace")}
              text="Pace of Play"
            />
            <ReviewAvgItem
              rating={getAverageRating("service")}
              text="Quality of Service"
            />
            <ReviewAvgItem
              rating={getAverageRating("overall")}
              text="Overall Experience"
            />
          </div>
          <div className="flex gap-2 items-top">
            <div>
              <Shield_Icon className="mb-1 inline" />
            </div>
            <div>
              <Text
                size={12}
                weight="semibold"
                color="black90"
                className="mb-[2px] mt-1"
              >
                100% Verified Reviews
              </Text>
              <Text size={11} leading={1.3} color="black60" className="">
                All reviews on {process.env.GATSBY_SITE_URL} are written by real
                golfers who have played this golf course
              </Text>
            </div>
          </div>
        </div>

        <div>
          <div className="flex flex-col gap-10 mb-6">
            {reviews
              .sort((a, b) => {
                if (a.created_at > b.created_at) return -1;
                return 1;
              })
              .slice(0, visible)
              .map((review) => (
                <ReviewItem key={review.id} data={review} />
              ))}
          </div>
          {visible < reviews.length && (
            <Button
              text={`Show all ${reviews.length} reviews`}
              color="grey7"
              invert
              size="medium"
              className="!w-full lg:!w-auto !px-4 !mb-0"
              onClick={() => {
                onShowModal();
              }}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default CourseReviews;
